import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaPrint, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

const LabourChargesReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [reportData, setReportData] = useState({
    fromDate: "",
    toDate: "",
    projectid: "",
    supplierid: "",
  });

  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    totalExpenditure: 0,
    totalAdvance: 0,
    totalOtherCharges: 0,
    totalNetAmount: 0,
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setReportData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!branchId) return;

    const fetchDropdowns = async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/labourcharge/projects`, {
            params: { branchid: branchId },
            headers: commonHeaders,
          }),
          axios.get(`${BASE_URL}/api/labourcharge/suppliers`, {
            params: { branchid: branchId },
            headers: commonHeaders,
          }),
        ]);

        if (pRes.data?.success) {
          setProjects(pRes.data.data || []);
        } else {
          toast.error(pRes.data?.message || "Failed to load projects");
        }

        if (sRes.data?.success) {
          setSuppliers(sRes.data.data || []);
        } else {
          toast.error(sRes.data?.message || "Failed to load suppliers");
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message);
      }
    };

    fetchDropdowns();
  }, [branchId, commonHeaders]);

  const validate = () => {
    if (!branchId) {
      toast.error("BranchId missing!");
      return false;
    }

    if (!reportData.fromDate || !reportData.toDate) {
      toast.error("Please select both From Date and To Date!");
      return false;
    }

    const f = new Date(reportData.fromDate);
    const t = new Date(reportData.toDate);

    if (f > t) {
      toast.error("From Date cannot be later than To Date!");
      return false;
    }

    return true;
  };

  const handleViewReport = async () => {
    if (!validate()) return;

    setLoading(true);
    setRows([]);
    setTotals({
      totalExpenditure: 0,
      totalAdvance: 0,
      totalOtherCharges: 0,
      totalNetAmount: 0,
    });

    try {
      const res = await axios.get(`${BASE_URL}/api/labourcharge/search`, {
        params: {
          branchid: branchId,
          fromDate: reportData.fromDate,
          toDate: reportData.toDate,
          projectid: reportData.projectid || 0,
          supplierid: reportData.supplierid || 0,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const data = res.data.data || [];
        setRows(data);

        if (res.data.totals) {
          setTotals({
            totalExpenditure: Number(res.data.totals.totalExpenditure || 0),
            totalAdvance: Number(res.data.totals.totalAdvance || 0),
            totalOtherCharges: Number(res.data.totals.totalOtherCharges || 0),
            totalNetAmount: Number(res.data.totals.totalNetAmount || 0),
          });
        } else {
          const calculatedTotals = data.reduce(
            (acc, r) => {
              acc.totalExpenditure += Number(r.totalExpenditure || 0);
              acc.totalAdvance += Number(r.advance || 0);
              acc.totalOtherCharges += Number(r.otherCharges || 0);
              acc.totalNetAmount += Number(r.netAmount || 0);
              return acc;
            },
            {
              totalExpenditure: 0,
              totalAdvance: 0,
              totalOtherCharges: 0,
              totalNetAmount: 0,
            }
          );

          setTotals(calculatedTotals);
        }

        toast.success("Report loaded!");
      } else {
        toast.error(res.data?.message || "Failed to load report");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!validate()) return;

    const projectid = reportData.projectid || 0;
    const supplierid = reportData.supplierid || 0;

    window.open(
      `/PaymentVouchers/LabourChargesReport_Print?branchid=${encodeURIComponent(
        branchId
      )}&fromDate=${encodeURIComponent(
        reportData.fromDate
      )}&toDate=${encodeURIComponent(
        reportData.toDate
      )}&projectid=${encodeURIComponent(
        projectid
      )}&supplierid=${encodeURIComponent(supplierid)}`
    );
  };

  const money = (n) => {
    const x = Number(n || 0);
    return x.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LABOUR CHARGES REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Labour Charges Report
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Report Criteria
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={reportData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Code <span className="text-gray-400">(Optional)</span>
                  </label>
                  <select
                    value={reportData.projectid}
                    onChange={(e) => handleInputChange("projectid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={reportData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supplier <span className="text-gray-400">(Optional)</span>
                  </label>
                  <select
                    value={reportData.supplierid}
                    onChange={(e) => handleInputChange("supplierid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={handleViewReport}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaEye />
                  {loading ? "Loading..." : "View"}
                </button>

                <button
                  onClick={handlePrint}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-800 border border-gray-300"
                  }`}
                >
                  <FaPrint />
                  Print
                </button>
              </div>
            </div>
          )}

          <div
            className={`p-4 rounded-lg border ${
              isDark ? "bg-gray-800/30 border-gray-600" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Report Results
              </h4>

              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"} space-x-4`}>
                <span>
                  Total Exp: <b>{money(totals.totalExpenditure)}</b>
                </span>
                <span>
                  Advance: <b>{money(totals.totalAdvance)}</b>
                </span>
                <span>
                  Other: <b>{money(totals.totalOtherCharges)}</b>
                </span>
                <span>
                  Net: <b>{money(totals.totalNetAmount)}</b>
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className={`min-w-full text-sm ${isDark ? "text-white" : "text-gray-900"}`}>
                <thead>
                  <tr className={`${isDark ? "bg-gray-700" : "bg-gray-200"}`}>
                    <th className="px-3 py-2 text-left">S.No</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Token No</th>
                    <th className="px-3 py-2 text-left">Project Name</th>
                    <th className="px-3 py-2 text-left">Location</th>
                    <th className="px-3 py-2 text-right">Total Expenditure</th>
                    <th className="px-3 py-2 text-right">Advance</th>
                    <th className="px-3 py-2 text-right">Other Charges</th>
                    <th className="px-3 py-2 text-right">Net Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className={`px-3 py-6 text-center ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {loading ? "Loading..." : "No data. Select filters and click View."}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "border-gray-700" : "border-gray-200"} border-b`}
                      >
                        <td className="px-3 py-2">{idx + 1}</td>
                        <td className="px-3 py-2">{r.date || ""}</td>
                        <td className="px-3 py-2">{r.supplier || ""}</td>
                        <td className="px-3 py-2">{r.tokenNo || ""}</td>
                        <td className="px-3 py-2">{r.projectName || ""}</td>
                        <td className="px-3 py-2">{r.location || ""}</td>
                        <td className="px-3 py-2 text-right">{money(r.totalExpenditure)}</td>
                        <td className="px-3 py-2 text-right">{money(r.advance)}</td>
                        <td className="px-3 py-2 text-right">{money(r.otherCharges)}</td>
                        <td className="px-3 py-2 text-right">{money(r.netAmount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default LabourChargesReport;